import { useEffect, useRef, useState } from "react";

/**
 * Hook de coordinación de pestañas con latidos y limpieza segura.
 *
 * - Usa sessionStorage para que el tabId sobreviva a recargas (no se duplica).
 * - Usa pagehide/pageshow/visibilitychange en lugar de beforeunload.
 * - Evita limpiar sesión inmediatamente después de loguear.
 * - Limpia de verdad al cerrar la última pestaña y abrir de nuevo.
 */
export function useTabHeartbeat(
  key = "app:tabs",         // mapa { tabId: timestamp }
  heartbeatInterval = 5000, // ms entre latidos
  ttl = 10000,              // ms para considerar “viva” una pestaña
  graceMs = 1200,           // margen entre lastExit y limpieza
  { clearAllOnFinalCleanup = true } = {} // true = borra TODO localStorage, false = solo tu namespace
) {
  const [isAnyTabAlive, setIsAnyTabAlive] = useState(true);
  const tabIdRef = useRef(null);

  // Identidad única por pestaña (sobrevive a recargas, pero no entre pestañas)
  if (!tabIdRef.current) {
    const selfKey = `${key}:self`;
    let id = sessionStorage.getItem(selfKey);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(selfKey, id);
    }
    tabIdRef.current = id;
  }

  useEffect(() => {
    const tabId = tabIdRef.current;
    const MAP_KEY = key;
    const LAST_EXIT_KEY = `${key}:lastExit`;
    const EXITING_FLAG = `${key}:exiting`; // bandera local (esta pestaña)

    const safeParse = (str) => {
      try { return JSON.parse(str); } catch { return {}; }
    };
    const readTabs = () => safeParse(localStorage.getItem(MAP_KEY) || "{}");
    const writeTabs = (tabs) => localStorage.setItem(MAP_KEY, JSON.stringify(tabs));
    const markLastExit = (t = Date.now()) => localStorage.setItem(LAST_EXIT_KEY, String(t));

    const cleanupNamespace = () => {
      if (clearAllOnFinalCleanup) {
        localStorage.clear(); // ⚠️ borra TODO, incluyendo tokens
        return;
      }
      localStorage.removeItem(MAP_KEY);
      localStorage.removeItem(LAST_EXIT_KEY);
      const toDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(`${key}:`)) toDelete.push(k);
      }
      toDelete.forEach((k) => localStorage.removeItem(k));
    };

    const pruneFresh = (tabs, now) => {
      const fresh = Object.fromEntries(
        Object.entries(tabs).filter(([, ts]) => now - ts < ttl)
      );
      return fresh;
    };

    const updateTabs = () => {
      const tabs = readTabs();
      tabs[tabId] = Date.now();
      writeTabs(tabs);
    };

    const scavenge = () => {
      const now = Date.now();
      const current = readTabs();
      const fresh = pruneFresh(current, now);
      if (Object.keys(fresh).length !== Object.keys(current).length) {
        writeTabs(fresh); // purga expirados
      }
      const alive = Object.values(fresh).some((ts) => now - ts < ttl);
      setIsAnyTabAlive(alive);

      const lastExit = Number(localStorage.getItem(LAST_EXIT_KEY) || "0");
      const exitingHere = sessionStorage.getItem(EXITING_FLAG) === "1";

      if (!alive && Object.keys(fresh).length === 0 && now - lastExit > graceMs && !exitingHere) {
        cleanupNamespace();
      }
    };

    // ---------- ARRANQUE SEGURO ----------
    updateTabs(); // siempre registro esta pestaña primero

    (() => {
      const now = Date.now();
      const current = readTabs();
      const fresh = pruneFresh(current, now);
      const lastExit = Number(localStorage.getItem(LAST_EXIT_KEY) || "0");

      // Caso: soy la única pestaña registrada
      if (Object.keys(fresh).length === 1 && fresh[tabId]) {
        if (lastExit && now - lastExit > graceMs) {
          cleanupNamespace();
          updateTabs(); // re-anoto tras limpiar
        }
      } else if (Object.keys(fresh).length !== Object.keys(current).length) {
        writeTabs(fresh); // purgo expirados
      }
    })();

    // ---------- Latido periódico ----------
    const beat = setInterval(updateTabs, heartbeatInterval);

    // ---------- Señales de ciclo de vida ----------
    const onPageHide = () => {
      sessionStorage.setItem(EXITING_FLAG, "1");
      const tabs = readTabs();
      delete tabs[tabId];
      writeTabs(tabs);
      markLastExit();
    };

    const onPageShow = () => {
      sessionStorage.removeItem(EXITING_FLAG);
      updateTabs();
      scavenge();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        sessionStorage.removeItem(EXITING_FLAG);
        updateTabs();
        scavenge();
      }
    };

    const onStorage = (e) => {
      if (e.key === MAP_KEY || e.key === LAST_EXIT_KEY) {
        scavenge();
      }
    };

    window.addEventListener("pagehide", onPageHide, { capture: true });
    window.addEventListener("pageshow", onPageShow, { capture: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);

    scavenge(); // estado inicial correcto

    return () => {
      clearInterval(beat);
      window.removeEventListener("pagehide", onPageHide, { capture: true });
      window.removeEventListener("pageshow", onPageShow, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
    };
  }, [key, heartbeatInterval, ttl, graceMs, clearAllOnFinalCleanup]);

  return isAnyTabAlive;
}





